import React, { useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Collapse,
  Flex,
  FormControl,
  IconButton,
  Input,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { ClipboardText, Plus, Trash } from 'phosphor-react';
import { IBaixaPresente } from '../../hooks/useEstatuto';
import {
  EMAIL_REGEX,
  cpfDaPlanilha,
  cpfValido,
  formatarCpf,
  lerTsv,
  sexoDoTexto,
  soDigitos,
} from './PessoasField';
import { respostaVaga } from './respostaVaga';

// Baixa (21/10): associados da constituição já vêm na lista (desmarcados); o presidente marca
// quem compareceu e inclui quem aderiu depois. Só os marcados vão para a Lista de Presença.
const VAZIA: IBaixaPresente = {
  nome: '',
  cpf: '',
  email: '',
  sexo: '',
  adimplente: true,
  presente: true,
};

export const marcados = (linhas: IBaixaPresente[]) => linhas.filter(p => p.presente);

// Nome curto ou "não sei" no lugar do nome.
const nomeRuim = (p: IBaixaPresente) => p.nome.trim().length < 3 || respostaVaga(p.nome);
/** Presente com nome, CPF, e-mail ou sexo faltando ou inválido (a linha fica marcada). */
const linhaRuim = (p: IBaixaPresente) =>
  nomeRuim(p) || !cpfValido(p.cpf) || !EMAIL_REGEX.test(p.email.trim()) || !p.sexo;
const fundoDaLinha = (p: IBaixaPresente) => (p.presente && linhaRuim(p) ? 'red.50' : undefined);

/** Problemas dos marcados (o back confere de novo ao enviar). */
export const problemasDosPresentes = (linhas: IBaixaPresente[]): string | null => {
  const presentes = marcados(linhas);
  if (!presentes.length) return 'Marque quem esteve na assembleia.';
  const ruins = presentes.filter(linhaRuim);
  if (ruins.length)
    return `${ruins.length} presente(s) com nome, CPF, e-mail ou sexo faltando ou inválido.`;
  const cpfs = presentes.map(p => soDigitos(p.cpf));
  if (new Set(cpfs).size !== cpfs.length) return 'Há CPF repetido entre os presentes.';
  const emails = presentes.map(p => p.email.trim().toLowerCase());
  if (new Set(emails).size !== emails.length)
    return 'Cada presente assina no próprio e-mail: há e-mail repetido.';
  return null;
};

/** O que vai para o back: só os marcados, sem a marca e com o CPF só em dígitos. */
export const paraEnvio = (linhas: IBaixaPresente[]) =>
  marcados(linhas).map(p => ({
    nome: p.nome,
    sexo: p.sexo,
    cpf: soDigitos(p.cpf),
    email: p.email,
    adimplente: p.adimplente,
  }));

interface PresentesFieldProps {
  value: IBaixaPresente[];
  onChange: (v: IBaixaPresente[]) => void;
}

export const PresentesField: React.FC<PresentesFieldProps> = ({ value, onChange }) => {
  const [colando, setColando] = useState(false);
  const [texto, setTexto] = useState('');

  const mudar = (i: number, campo: keyof IBaixaPresente, v: unknown) =>
    onChange(value.map((p, j) => (j === i ? { ...p, [campo]: v } : p)));
  const todos = (presente: boolean) => onChange(value.map(p => ({ ...p, presente })));

  // Colagem da planilha: Nome, CPF, E-mail, Sexo (cabeçalho opcional).
  const incluirColagem = () => {
    const novos = lerTsv(texto)
      .filter(c => c.some(Boolean))
      .filter((c, i) => !(i === 0 && /^nome/i.test(c[0])))
      .map(c => ({
        ...VAZIA,
        nome: c[0] ?? '',
        cpf: cpfDaPlanilha(c[1] ?? ''),
        email: c[2] ?? '',
        sexo: sexoDoTexto(c[3] ?? ''),
      }));
    onChange([...value, ...novos]);
    setTexto('');
    setColando(false);
  };

  const problema = problemasDosPresentes(value);

  // FormControl próprio: os inputs da tabela não herdam o "obrigatório/inválido" do campo.
  return (
    <FormControl>
      <Flex gap={2} wrap="wrap" mb={3} align="center">
        <Text fontSize="sm" color="gray.600" mr="auto">
          {marcados(value).length} presente(s) de {value.length} na lista
        </Text>
        <Button size="xs" variant="outline" onClick={() => todos(true)}>
          Marcar todos
        </Button>
        <Button size="xs" variant="outline" onClick={() => todos(false)}>
          Desmarcar todos
        </Button>
        <Button
          size="xs"
          variant="outline"
          leftIcon={<ClipboardText size={14} />}
          onClick={() => setColando(!colando)}
        >
          Colar da planilha
        </Button>
        <Button
          size="xs"
          colorScheme="blue"
          leftIcon={<Plus size={14} weight="bold" />}
          onClick={() => onChange([...value, { ...VAZIA }])}
        >
          Adicionar pessoa
        </Button>
      </Flex>

      <Collapse in={colando} animateOpacity>
        <Box mb={3}>
          <Text fontSize="xs" color="gray.500" mb={1}>
            Cole as linhas da planilha com as colunas Nome, CPF, E-mail e Sexo (F ou M).
          </Text>
          <Textarea
            aria-label="Colar presentes"
            value={texto}
            onChange={e => setTexto(e.target.value)}
            minH="100px"
            fontSize="xs"
          />
          <Button
            size="xs"
            mt={2}
            colorScheme="blue"
            onClick={incluirColagem}
            isDisabled={!texto.trim()}
          >
            Incluir na lista
          </Button>
        </Box>
      </Collapse>

      {value.length > 0 && (
        <Box overflowX="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th px={2}>Presente</Th>
                <Th px={2}>Nome</Th>
                <Th px={2}>CPF</Th>
                <Th px={2}>E-mail</Th>
                <Th px={2}>Sexo</Th>
                <Th px={2}>Adimplente</Th>
                <Th px={2} />
              </Tr>
            </Thead>
            <Tbody>
              {value.map((p, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Tr key={i} opacity={p.presente ? 1 : 0.55} bg={fundoDaLinha(p)}>
                  <Td px={2}>
                    <Checkbox
                      aria-label={`Presente: ${p.nome || `linha ${i + 1}`}`}
                      isChecked={!!p.presente}
                      onChange={e => mudar(i, 'presente', e.target.checked)}
                    />
                  </Td>
                  <Td px={2} minW="180px">
                    <Input
                      size="sm"
                      id={`presente-${i}-nome`}
                      aria-label={`Nome (linha ${i + 1})`}
                      value={p.nome}
                      isInvalid={!!p.presente && nomeRuim(p)}
                      onChange={e => mudar(i, 'nome', e.target.value)}
                    />
                  </Td>
                  <Td px={2} minW="140px">
                    <Input
                      size="sm"
                      id={`presente-${i}-cpf`}
                      aria-label={`CPF (linha ${i + 1})`}
                      value={formatarCpf(p.cpf) || p.cpf}
                      isInvalid={!!p.presente && !!p.cpf && !cpfValido(p.cpf)}
                      onChange={e => mudar(i, 'cpf', soDigitos(e.target.value).slice(0, 11))}
                    />
                  </Td>
                  <Td px={2} minW="190px">
                    <Input
                      size="sm"
                      id={`presente-${i}-email`}
                      aria-label={`E-mail (linha ${i + 1})`}
                      value={p.email}
                      isInvalid={!!p.presente && !EMAIL_REGEX.test(p.email.trim())}
                      onChange={e => mudar(i, 'email', e.target.value)}
                    />
                  </Td>
                  <Td px={2} minW="70px">
                    <Select
                      size="sm"
                      id={`presente-${i}-sexo`}
                      aria-label={`Sexo (linha ${i + 1})`}
                      value={p.sexo}
                      isInvalid={!!p.presente && !p.sexo}
                      placeholder="-"
                      onChange={e => mudar(i, 'sexo', e.target.value)}
                    >
                      <option value="F">F</option>
                      <option value="M">M</option>
                    </Select>
                  </Td>
                  <Td px={2} textAlign="center">
                    <Checkbox
                      aria-label={`Adimplente: ${p.nome || `linha ${i + 1}`}`}
                      isChecked={p.adimplente}
                      onChange={e => mudar(i, 'adimplente', e.target.checked)}
                    />
                  </Td>
                  <Td px={2}>
                    <IconButton
                      aria-label={`Remover ${p.nome || `linha ${i + 1}`}`}
                      size="xs"
                      variant="ghost"
                      icon={<Trash size={14} />}
                      onClick={() => onChange(value.filter((_, j) => j !== i))}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {problema && value.length > 0 && (
        <Alert status="warning" mt={3} borderRadius="md" fontSize="xs" py={2}>
          <AlertIcon />
          {problema}
        </Alert>
      )}
    </FormControl>
  );
};
